import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

import { API_BASE, IMAGE_BASE_URL as BASE_PUBLIC } from "../config/constants";
import { cacheProducts, getCachedProducts } from "../utils/productListCache";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { listOfflineProducts, reorderOfflineProducts } from "../offline/productStore";

const buildImageUrl = (item) => {
  const raw = item?.image_path || item?.photo_path || item?.image || item?.photo || "";
  if (!raw) return "";
  if (/^(?:https?:\/\/|data:|blob:)/i.test(raw)) return raw;
  return `${BASE_PUBLIC}${String(raw).replace(/^\/?/, "")}`;
};

const ProductThumb = ({ item }) => {
  const resolvedImageUrl = buildImageUrl(item);
  const imageUrl = isFullOfflineModeEnabled() && !/^(?:data:|blob:)/i.test(resolvedImageUrl)
    ? ""
    : resolvedImageUrl;
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [imageUrl]);
  return imageUrl && !failed
    ? <Img src={imageUrl} alt={item?.name || "Product"} onError={() => setFailed(true)} />
    : <Avatar aria-hidden="true">{String(item?.name || "?").slice(0, 1).toUpperCase()}</Avatar>;
};

const sortProducts = (list = []) =>
  [...list].sort((a, b) => {
    const aOrder = Number(a?.displayorder ?? a?.display_order ?? Number.MAX_SAFE_INTEGER);
    const bOrder = Number(b?.displayorder ?? b?.display_order ?? Number.MAX_SAFE_INTEGER);
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });

const ProductReorderPage = () => {
  const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [products, setProducts] = useState([]);
  const [original, setOriginal] = useState([]);

  const hasChanges = useMemo(() => {
    if (products.length !== original.length) return true;
    return products.some((item, i) => item?.id !== original[i]?.id);
  }, [products, original]);

  const fetchProducts = useCallback(async () => {
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      return;
    }
    setLoading(true);
    try {
      if (isFullOfflineModeEnabled()) {
        const offlineProducts = await listOfflineProducts(user.id);
        const sorted = sortProducts(Array.isArray(offlineProducts) ? offlineProducts : []);
        setProducts(sorted);
        setOriginal(sorted);
        return;
      }
      const cachedProducts = await getCachedProducts(user.id, "master");
      if (cachedProducts?.length) {
        const sortedCached = sortProducts(cachedProducts);
        setProducts(sortedCached);
        setOriginal(sortedCached);
      }
      const response = await fetch(`${API_BASE}/product/list/master`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: user.id }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.message || "Failed to load products");
      const sorted = sortProducts(Array.isArray(json?.list) ? json.list : []);
      setProducts(sorted);
      setOriginal(sorted);
      cacheProducts(user.id, json?.list || [], "master");
    } catch (error) {
      toast.error(error?.message || "Unable to load products");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const moveBy = (index, delta) => {
    const next = index + delta;
    if (next < 0 || next >= products.length) return;
    setProducts((prev) => {
      const copy = [...prev];
      const [picked] = copy.splice(index, 1);
      copy.splice(next, 0, picked);
      return copy;
    });
  };

  const cancelEdit = () => {
    setProducts(original);
    setEditing(false);
  };

  const saveOrder = async () => {
    if (!hasChanges) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const fullOfflineMode = isFullOfflineModeEnabled();
      const items = products
        .map((item, idx) => ({
          productid: fullOfflineMode ? String(item?.id || "").trim() : Number(item?.id),
          displayorder: idx + 1,
        }))
        .filter((item) => fullOfflineMode
          ? Boolean(item.productid)
          : Number.isFinite(item.productid));

      if (fullOfflineMode) {
        await reorderOfflineProducts(user.id, items);
      } else {
        const response = await fetch(`${API_BASE}/product/reorder`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: user.id, items }),
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok || json?.success === false) {
          throw new Error(json?.message || "Failed to save product order");
        }
      }
      const reordered = products.map((product, index) => ({
        ...product,
        displayorder: index + 1,
        display_order: index + 1,
      }));
      setProducts(reordered);
      setOriginal(reordered);
      setEditing(false);
      toast.success("Product order updated");
    } catch (error) {
      toast.error(error?.message || "Failed to save product order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <Header>
        <div>
          <h1>Product Reorder</h1>
          <p>Set product sequence for menu display and billing order.</p>
        </div>
        <Row>
          {!editing ? (
            <Btn type="button" onClick={() => setEditing(true)} disabled={loading || !products.length}>
              Edit
            </Btn>
          ) : (
            <>
              <Btn type="button" onClick={cancelEdit} disabled={saving}>Cancel</Btn>
              <Btn type="button" $primary onClick={saveOrder} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Btn>
            </>
          )}
        </Row>
      </Header>

      <Card>
        {loading ? (
          <Empty>Loading products...</Empty>
        ) : products.length === 0 ? (
          <Empty>No products found.</Empty>
        ) : (
          <List>
            {products.map((item, idx) => {
              return (
                <Item key={`${item?.id}-${idx}`}>
                  <Order>{idx + 1}</Order>
                  <ProductThumb item={item} />
                  <Info>
                    <strong>{item?.name || "-"}</strong>
                    <small>{item?.productcategoryname || "Uncategorized"}</small>
                  </Info>
                  {editing && (
                    <Row>
                      <MoveBtn type="button" onClick={() => moveBy(idx, -1)} disabled={idx === 0}>
                        <FaArrowUp />
                      </MoveBtn>
                      <MoveBtn
                        type="button"
                        onClick={() => moveBy(idx, 1)}
                        disabled={idx === products.length - 1}
                      >
                        <FaArrowDown />
                      </MoveBtn>
                    </Row>
                  )}
                </Item>
              );
            })}
          </List>
        )}
      </Card>
    </Page>
  );
};

export default ProductReorderPage;

const Page = styled.div`
  min-height: 100vh;
  padding: 24px;
  background: #f4f7fb;
  display: grid;
  align-content: start;
  gap: 16px;
  @media(max-width:650px){padding:14px;}
`;

const Header = styled.div`
  background: linear-gradient(135deg,var(--app-primary),var(--app-primary-hover));
  color: #fff;
  border-radius: 22px;
  padding: 27px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  box-shadow:0 14px 30px rgba(var(--app-primary-rgb), .17);
  h1 { margin: 0; font-size: 2rem; }
  p { margin: 6px 0 0; color: #dff3ff; }
  @media(max-width:600px){padding:22px 18px;align-items:stretch;flex-direction:column;}
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow:0 6px 18px rgba(15,23,42,.04);
`;

const List = styled.div`
  display: grid;
  gap: 9px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 30px 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  padding: 11px;
  background:#fff;
  transition:.18s ease;
  &:hover{border-color:#afd1e3;box-shadow:0 5px 14px rgba(15,23,42,.05);}
`;

const Order = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 800;
  display: grid;
  place-items: center;
`;

const Img = styled.img`
  width: 42px;
  height: 42px;
  border-radius: 8px;
  object-fit: cover;
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-weight: 800;
  display: grid;
  place-items: center;
`;

const Info = styled.div`
  min-width: 0;
  display: grid;
  strong {
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  small { color: #64748b; }
`;

const MoveBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  cursor: pointer;
`;

const Btn = styled.button`
  height: 38px;
  border-radius: 9px;
  padding: 0 12px;
  border: 1px solid ${({ $primary }) => ($primary ? "#fff" : "rgba(255,255,255,.65)")};
  background: ${({ $primary }) => ($primary ? "#fff" : "rgba(255,255,255,.13)")};
  color: ${({ $primary }) => ($primary ? "var(--app-primary)" : "#fff")};
  font-weight: 700;
  cursor: pointer;
`;

const Empty = styled.div`
  color: #64748b;
  min-height:180px;display:grid;place-items:center;text-align:center;
`;
